/**
 * menu-item-view-runtime — runtime dla przypisań widoku pozycji menu tenanta.
 *
 * Pozycja menu (`MenuItem`, type='module') może przypiąć: layout listy (`listRef`),
 * layout formularza (`viewRef`) oraz filtry i sortowanie stałe (`view.baseFilters`
 * / `view.baseSort`). Te helpery tłumaczą przypisania na parametry runtime strony
 * klienta:
 *   - `baseFiltersToHeaderFilters` — filtry stałe → format `ColumnHeaderFilter`
 *     (ten sam, którym backend list rekordów filtruje w WHERE). Dołączane WYŁĄCZNIE
 *     przy żądaniu do serwera (poza reactowym stanem), więc są niewidoczne
 *     i nieusuwalne dla usera, łączone AND z filtrami które user sam dołoży.
 *   - `baseSortToParams` — sortowanie stałe → para `?sort=&order=` (backend list
 *     wspiera pojedyncze pole; wielopolowe sortowanie = ograniczenie MVP, bierzemy
 *     pierwszą pozycję).
 *
 * `FilterConfig.field` / `SortConfig.column` przechowują SLUG pola (edytor filtrów
 * stałych zapisuje `id` = slug pola encji), więc idą do backendu wprost — bez
 * tłumaczenia column-id → fieldSlug jak przy „szybkich filtrach".
 */

import type { FilterConfig, SortConfig } from '@saas/shared';

import type { ColumnHeaderFilter } from '@/components/configurable-table/types';
import { isCurrentUserFilterToken, resolveFilterVariable } from '@/lib/list-filter-variables';

/**
 * Konwertuje filtry stałe pozycji menu na `ColumnHeaderFilter[]` (mode condition
 * dla większości operatorów; `in` → mode values; `is_null`/`is_not_null` → condition
 * bez wartości). Wpisy z pustą wartością (operator wymagający wartości) i bez pola
 * są pomijane — backend i tak by je odrzucił.
 */
export function baseFiltersToHeaderFilters(
  baseFilters: FilterConfig[] | undefined,
  /**
   * Id zalogowanego użytkownika — podstawiane za `@userId` (i dawne `@ja`); brak → taki warunek
   * pominięty.
   */
  userId?: string | null,
): ColumnHeaderFilter[] {
  if (!baseFilters || baseFilters.length === 0) return [];

  const out: ColumnHeaderFilter[] = [];
  for (const raw of baseFilters) {
    if (!raw.field) continue;
    const isCurrentUser = isCurrentUserFilterToken(raw.value);
    const f = isCurrentUser ? { ...raw, value: resolveFilterVariable(raw.value, userId) } : raw;
    // Znacznik bez znanego użytkownika — pomijamy, zamiast słać do serwera napis bez dopasowania.
    if (isCurrentUser && f.value === null) continue;

    if (f.operator === 'is_null' || f.operator === 'is_not_null') {
      out.push({ field: f.field, mode: 'condition', operator: f.operator });
      continue;
    }

    if (f.operator === 'in') {
      // Edytor filtrów zapisuje wartości `in` jako string rozdzielony przecinkami.
      const values = String(f.value ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (values.length === 0) continue;
      out.push({ field: f.field, mode: 'values', selectedValues: values });
      continue;
    }

    const conditionValue = f.value == null ? '' : String(f.value);
    if (conditionValue === '') continue;
    out.push({ field: f.field, mode: 'condition', operator: f.operator, conditionValue });
  }

  return out;
}

/**
 * Zwraca parę `{ sort, order }` z pierwszej pozycji sortowania stałego, lub `null`
 * gdy brak. Backend list rekordów sortuje po pojedynczym polu — kolejne pozycje
 * `baseSort` są ignorowane (ograniczenie MVP).
 */
export function baseSortToParams(
  baseSort: SortConfig[] | undefined,
): { sort: string; order: 'asc' | 'desc' } | null {
  const first = baseSort?.[0];
  if (!first || !first.column) return null;
  return { sort: first.column, order: first.direction };
}
